import React, { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs, { Dayjs } from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import styled from 'styled-components';
import { ButtonIcon, Icon } from 'components';
import { theme, toRem } from 'design';
import { useKeyPressed } from 'hooks';

dayjs.extend(weekday);
dayjs.extend(weekOfYear);

interface IWeeklyCalendarProps {
  customizedVisibleWeek?: IWeekView[];
  dataCy?: string;
  expandedDay?: React.ReactNode;
  futureWeekMax?: number;
  isLoading?: boolean;
  onCalNavigation?: (days?: IWeekView[]) => void;
  onDayClick?: (day?: IWeekView) => void;
  previousWeekMax?: number;
  selectedDay?: Dayjs;
}

export interface IWeekView {
  weekday: Dayjs;
  includeCheck?: boolean;
}

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  max-width: ${toRem(theme.breakpoint.large)};
  margin: 0 auto;
  background-color: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  padding: ${toRem(40)};
`;

const Month = styled.h3`
  font-size: ${toRem(16)};
  font-weight: ${theme.font.weight.semiBold};
  text-transform: uppercase;
  padding-bottom: ${toRem(24)};
`;

const Week = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
`;

const Day = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};
  margin-right: ${toRem(8)};
  padding: ${toRem(14)} ${toRem(16)};
  width: ${toRem(80)};
  height: ${toRem(80)};
  position: relative;

  &:hover,
  &:focus {
    background: ${theme.color.neutral[200]};
    cursor: pointer;
  }

  &:focus-visible {
    outline: none;
  }

  &:active,
  &.is-active {
    background-color: ${theme.color.secondary[400]};

    p {
      color: ${theme.color.white};
    }
  }
`;

const Weekday = styled.p`
  color: ${theme.color.neutral[500]};
  font-size: ${toRem(12)};
  font-weight: ${theme.font.weight.semiBold};
  padding-bottom: ${toRem(8)};
  text-transform: uppercase;
`;

const Date = styled.p`
  color: ${theme.color.neutral[900]};
  font-size: ${toRem(32)};
  font-weight: ${theme.font.weight.regular};
`;

const StyledButtonIcon = styled(ButtonIcon)`
  border: none;

  &:first-child {
    margin-right: ${toRem(8)};
  }

  &:hover,
  &:focus {
    background: ${theme.color.neutral[200]};
    border-radius: ${toRem(4)};
  }

  &:disabled,
  &:disabled:hover {
    background: none;
    cursor: not-allowed;
  }
`;

const ExpandedDayInfo = styled.section`
  margin-top: ${toRem(24)};
  width: 100%;
`;

const CheckMarkIconBox = styled.div`
  position: absolute;
  top: ${toRem(5)};
  right: ${toRem(5)};
`;

const buildCalendarDates = (date: dayjs.Dayjs): IWeekView[] => {
  const dates: IWeekView[] = [];
  const diffDays = date.startOf('day').diff(dayjs().startOf('day'), 'day') % 7; // check if the remainder of dividing the number by 7
  const modDays = diffDays >= -3 ? diffDays : diffDays + 7; // get mod days to build the calendar

  dates.push({ weekday: date.add(-3 - modDays, 'day') });
  dates.push({ weekday: date.add(-2 - modDays, 'day') });
  dates.push({ weekday: date.add(-1 - modDays, 'day') });
  dates.push({ weekday: date.add(0 - modDays, 'day') });
  dates.push({ weekday: date.add(1 - modDays, 'day') });
  dates.push({ weekday: date.add(2 - modDays, 'day') });
  dates.push({ weekday: date.add(3 - modDays, 'day') });

  return dates;
};

const getMonthNameDisplay = (first: Dayjs, last: Dayjs) => (first.isSame(last, 'month') ? first.format('MMMM YYYY') : `${first.format('MMMM YYYY')} - ${last.format('MMMM YYYY')}`);

const WeeklyCalendar = ({ dataCy = 'weekly-calendar', expandedDay, isLoading, onCalNavigation, onDayClick, selectedDay, previousWeekMax = 4, futureWeekMax = 0, customizedVisibleWeek }: IWeeklyCalendarProps) => {
  const [visibleWeek, setVisibleWeek] = useState<IWeekView[]>(buildCalendarDates(selectedDay || dayjs()));
  const [activeDay, setActiveDay] = useState<Dayjs | null>(selectedDay || null);
  const [updateWeek, setUpdateWeek] = useState<boolean>(true);

  useEffect(() => {
    if (updateWeek && onCalNavigation) onCalNavigation(visibleWeek);
    if (!updateWeek) setUpdateWeek(true);
  }, [visibleWeek]);

  useEffect(() => {
    if (customizedVisibleWeek?.length) {
      setUpdateWeek(false);
      setVisibleWeek(customizedVisibleWeek);
    }
  }, [customizedVisibleWeek]);

  const onDaySelect = (day: IWeekView) => {
    setActiveDay(day.weekday);
    onDayClick?.(day);
  };

  const onPreviousWeekClick = (date: IWeekView) => {
    setVisibleWeek(buildCalendarDates(date.weekday.subtract(4, 'days')));
  };

  const onNextWeekClick = (date: IWeekView) => {
    setVisibleWeek(buildCalendarDates(date.weekday.add(4, 'days')));
  };

  const enterKeyPress = useKeyPressed((event: KeyboardEvent) => event.key === 'Enter');

  const displayWeek = useMemo(
    () =>
      visibleWeek.map((day: IWeekView, index: number) => {
        const isSelectedDay = activeDay && day?.weekday?.isSame(activeDay, 'day');

        return isLoading ? (
          <Day key={`day-${index}-loading`}>
            <Weekday>
              <Skeleton width={50} />
            </Weekday>
            <Skeleton width={30} />
          </Day>
        ) : (
          <React.Fragment key={`day-${index}`}>
            <Day
              data-cy={`${dataCy}-day`}
              tabIndex={0}
              role="button"
              className={isSelectedDay && 'is-active'}
              onClick={() => onDaySelect(day)}
              onKeyUp={() => {
                if (enterKeyPress) onDaySelect(day);
              }}
            >
              {day.includeCheck && (
                <CheckMarkIconBox>
                  <Icon dataCy={`${dataCy}-day-checkmark`} icon="CheckmarkThick" fill={isSelectedDay ? theme.color.white : theme.color.secondary[400]} />
                </CheckMarkIconBox>
              )}
              <Weekday>{day.weekday.format('ddd')}</Weekday>
              <Date>{day.weekday.format('D')}</Date>
            </Day>
          </React.Fragment>
        );
      }),
    [visibleWeek, activeDay, enterKeyPress, isLoading]
  );

  return (
    <Layout data-cy={dataCy}>
      <Month data-cy={`${dataCy}-month`}>{isLoading ? <Skeleton width={100} /> : getMonthNameDisplay(visibleWeek[0].weekday, visibleWeek[(visibleWeek?.length ?? 0) - 1].weekday)}</Month>
      <Week
        data-cy={`${dataCy}-week-view`}
        aria-label={!!visibleWeek.length && `Calendar view for the week of ${visibleWeek[0].weekday.format('MMMM D YYYY')} to ${visibleWeek[(visibleWeek?.length ?? 0) - 1].weekday.format('MMMM D YYYY')}`}
      >
        <StyledButtonIcon
          data-cy={`${dataCy}-previous-week-btn`}
          aria-label="Previous week"
          icon="Previous"
          type="button"
          appearance="white"
          onClick={() => onPreviousWeekClick(visibleWeek[0])}
          isLoading={isLoading}
          disabled={visibleWeek[0].weekday.diff(dayjs(), 'weeks') <= -Math.abs(previousWeekMax)}
        />
        {displayWeek}
        <StyledButtonIcon
          data-cy={`${dataCy}-next-week-btn`}
          aria-label="Next week"
          icon="Next"
          type="button"
          appearance="white"
          onClick={() => onNextWeekClick(visibleWeek[(visibleWeek?.length ?? 0) - 1])}
          isLoading={isLoading}
          disabled={visibleWeek[0].weekday.diff(dayjs(), 'weeks') >= (Math.abs(futureWeekMax) > 0 ? Math.abs(futureWeekMax) - 1 : 0)}
        />
      </Week>
      {expandedDay && activeDay && <ExpandedDayInfo data-cy={`${dataCy}-expanded-day-info`}>{expandedDay}</ExpandedDayInfo>}
    </Layout>
  );
};

export default WeeklyCalendar;
